import React from "react";

import { Box, Chip, Grid, Typography } from "@mui/material";

import Header from "../../components/Header";
import DashboardCard from "../../components/DashboardCard";
import ThemedDataGrid from "../../components/ThemedDataGrid";

import { useTenant } from "../../context/TenantContext";

import useKnowledgeCoverageDashboard from "../../hooks/useKnowledgeCoverageDashboard";
import StatusChip from "../../components/StatusChip";

const KnowledgeCoverageDashboard = () => {
  const { tenantId } = useTenant();

  const { data, loading, error } = useKnowledgeCoverageDashboard({
    tenantId,
  });

  if (!tenantId) {
    return (
      <Box>
        <Header
          title="KNOWLEDGE COVERAGE"
          subtitle="Select a tenant to view who has read and understood assigned SOPs"
        />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box>
        <Header
          title="KNOWLEDGE COVERAGE"
          subtitle="Loading knowledge coverage insights..."
        />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box>
        <Header
          title="KNOWLEDGE COVERAGE"
          subtitle="Failed to load knowledge coverage dashboard"
        />
      </Box>
    );
  }

  const departmentColumns = [
    {
      field: "departmentName",
      headerName: "Department",
      flex: 1.4,
      minWidth: 180,
    },
    {
      field: "assignedCount",
      headerName: "Assigned",
      flex: 0.8,
      minWidth: 110,
    },
    {
      field: "notViewedCount",
      headerName: "Not Viewed",
      flex: 0.8,
      minWidth: 120,
    },
    {
      field: "readOnlyCount",
      headerName: "Read Only",
      flex: 0.8,
      minWidth: 110,
    },
    {
      field: "understoodCount",
      headerName: "Understood",
      flex: 0.9,
      minWidth: 130,
    },
    {
      field: "coveragePercent",
      headerName: "Coverage",
      flex: 0.8,
      minWidth: 120,
      renderCell: ({ value }) => <CoverageChip value={value} />,
    },
  ];

  const userColumns = [
    {
      field: "userName",
      headerName: "User",
      flex: 1.2,
      minWidth: 170,
    },
    {
      field: "assignedCount",
      headerName: "Assigned",
      flex: 0.7,
      minWidth: 100,
    },
    {
      field: "notViewedCount",
      headerName: "Not Viewed",
      flex: 0.8,
      minWidth: 120,
    },
    {
      field: "readOnlyCount",
      headerName: "Read Only",
      flex: 0.8,
      minWidth: 110,
    },
    {
      field: "understoodCount",
      headerName: "Understood",
      flex: 0.9,
      minWidth: 130,
    },
    {
      field: "outstandingCount",
      headerName: "Outstanding",
      flex: 0.9,
      minWidth: 130,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 160,
      renderCell: ({ value }) => <KnowledgeStatusChip status={value} />,
    },
  ];

  const sopColumns = [
    {
      field: "sopTitle",
      headerName: "SOP",
      flex: 1.4,
      minWidth: 200,
    },
    {
      field: "departmentName",
      headerName: "Department",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "assignedCount",
      headerName: "Assigned",
      flex: 0.7,
      minWidth: 100,
    },
    {
      field: "notViewedCount",
      headerName: "Not Viewed",
      flex: 0.8,
      minWidth: 120,
    },
    {
      field: "readOnlyCount",
      headerName: "Read Only",
      flex: 0.8,
      minWidth: 110,
    },
    {
      field: "understoodCount",
      headerName: "Understood",
      flex: 0.9,
      minWidth: 130,
    },
    {
      field: "coveragePercent",
      headerName: "Coverage",
      flex: 0.8,
      minWidth: 120,
      renderCell: ({ value }) => <CoverageChip value={value} />,
    },
  ];

  const recentlyUpdatedColumns = [
    {
      field: "sopTitle",
      headerName: "SOP",
      flex: 1.4,
      minWidth: 200,
    },
    {
      field: "version",
      headerName: "Version",
      flex: 0.6,
      minWidth: 100,
    },
    {
      field: "updatedAt",
      headerName: "Updated",
      flex: 0.8,
      minWidth: 130,
    },
    {
      field: "assignedCount",
      headerName: "Assigned",
      flex: 0.7,
      minWidth: 100,
    },
    {
      field: "outstandingCount",
      headerName: "Outstanding",
      flex: 0.8,
      minWidth: 130,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 160,
      renderCell: ({ value }) => <KnowledgeStatusChip status={value} />,
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header
          title="KNOWLEDGE COVERAGE"
          subtitle="View who has read, understood, or not yet viewed assigned SOPs"
        />
      </Box>

      <Grid container spacing={2}>
        <DashboardCard
          cardTitle="Total Assignments"
          xs={12}
          sm={6}
          md={2.4}
          height="140px"
        >
          <StatValue value={data.summary.totalAssignments} />
        </DashboardCard>

        <DashboardCard
          cardTitle="Not Viewed"
          xs={12}
          sm={6}
          md={2.4}
          height="140px"
        >
          <StatValue value={data.summary.notViewed} />
        </DashboardCard>

        <DashboardCard
          cardTitle="Read Only"
          xs={12}
          sm={6}
          md={2.4}
          height="140px"
        >
          <StatValue value={data.summary.readOnly} />
        </DashboardCard>

        <DashboardCard
          cardTitle="Understood"
          xs={12}
          sm={6}
          md={2.4}
          height="140px"
        >
          <StatValue value={data.summary.understood} />
        </DashboardCard>

        <DashboardCard
          cardTitle="Knowledge Coverage"
          xs={12}
          sm={6}
          md={2.4}
          height="140px"
        >
          <StatValue value={`${data.summary.knowledgeCoveragePercent}%`} />
        </DashboardCard>

        <DashboardCard
          cardTitle="Department Knowledge Coverage"
          xs={12}
          md={12}
          height="420px"
        >
          <ThemedDataGrid
            rows={data.departmentRows}
            columns={departmentColumns}
            hideFooter
            disableRowSelectionOnClick
          />
        </DashboardCard>

        <DashboardCard
          cardTitle="Users Requiring Attention"
          xs={12}
          md={6}
          height="420px"
        >
          <ThemedDataGrid
            rows={data.userRows}
            columns={userColumns}
            hideFooter
            disableRowSelectionOnClick
          />
        </DashboardCard>

        <DashboardCard cardTitle="SOP Engagement" xs={12} md={6} height="420px">
          <ThemedDataGrid
            rows={data.sopRows}
            columns={sopColumns}
            hideFooter
            disableRowSelectionOnClick
          />
        </DashboardCard>

        <DashboardCard
          cardTitle="Recently Updated SOPs Awaiting Review"
          xs={12}
          md={12}
          height="420px"
        >
          <ThemedDataGrid
            rows={data.recentlyUpdatedRows}
            columns={recentlyUpdatedColumns}
            hideFooter
            disableRowSelectionOnClick
          />
        </DashboardCard>
      </Grid>
    </Box>
  );
};

const StatValue = ({ value }) => (
  <Box display="flex" alignItems="center" justifyContent="center" height="100%">
    <Typography variant="h2" fontWeight="bold">
      {value}
    </Typography>
  </Box>
);

const CoverageChip = ({ value }) => {
  let tone = "danger";

  if (value >= 90) tone = "success";
  else if (value >= 60) tone = "gold";
  else if (value >= 30) tone = "teal";

  return (
    <StatusChip
      label={`${value}%`}
      tone={tone}
    />
  );
};

const KnowledgeStatusChip = ({ status }) => {
  const map = {
    Complete: "success",
    "In progress": "teal",
    "Attention needed": "gold",
    "Review required": "gold",
    "Not started": "danger",
  };

  return (
    <StatusChip
      label={status}
      tone={map[status] || "default"}
    />
  );
};

export default KnowledgeCoverageDashboard;